import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  resolveMeasurementProvider,
  PROVIDER_GOOGLE,
} from "@/lib/measurementProvider";
import GoogleWorkspace from "./GoogleWorkspace";

/**
 * MeasurementWorkspace — provider-aware entry point.
 *
 * Rules:
 *   - Consults `/api/measurements/config` + frontend flag + browser key.
 *   - If Google is chosen, renders the Google workspace inside a fullscreen dialog.
 *   - Otherwise renders a "Google workspace is disabled" hint. The existing
 *     Mapbox measurement flow remains available via the SmartEstimator's
 *     typed measurement dialog and is unchanged.
 */
export default function MeasurementWorkspace({
  open,
  onClose,
  propertyAddress,
  propertyLat,
  propertyLng,
  serviceCategory,
  onSaved,
  // Iter D.1 — estimate context (optional)
  estimateId,
  estimateLineItems,
  targetLineItemIndex,
  onLinked,
  // Iter57.2 — reuse focus (optional)
  initialFocusMeasurementId,
}) {
  const [decision, setDecision] = useState(null);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    resolveMeasurementProvider({ force: true }).then((d) => {
      if (mounted) setDecision(d);
    });
    return () => {
      mounted = false;
    };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent
        className="max-w-[95vw] w-[95vw] h-[92vh] p-4"
        data-testid="measurement-workspace-dialog"
      >
        <DialogHeader>
          <DialogTitle>Measurement Workspace</DialogTitle>
        </DialogHeader>
        {decision === null && (
          <div className="p-4 text-sm text-zinc-500" data-testid="workspace-loading">
            Resolving map provider…
          </div>
        )}
        {decision && decision.provider === PROVIDER_GOOGLE && (
          <div className="h-full overflow-hidden">
            <GoogleWorkspace
              propertyAddress={propertyAddress}
              propertyLat={propertyLat}
              propertyLng={propertyLng}
              serviceCategory={serviceCategory}
              onSaved={(m) => {
                if (onSaved) onSaved(m);
              }}
              onClose={onClose}
              solarEnabled={decision.google_solar_enabled}
              estimateId={estimateId}
              estimateLineItems={estimateLineItems}
              targetLineItemIndex={targetLineItemIndex}
              onLinked={onLinked}
              initialFocusMeasurementId={initialFocusMeasurementId}
            />
          </div>
        )}
        {decision && decision.provider !== PROVIDER_GOOGLE && (
          <div className="p-6 text-sm space-y-3" data-testid="workspace-mapbox-fallback">
            <p className="text-zinc-700">
              The Google measurement workspace is currently disabled ({" "}
              <code className="text-xs">{decision.reason}</code>).
            </p>
            <p className="text-zinc-500">
              You can still add measurements with the existing tools. Close this dialog and use
              the &ldquo;Add measurement&rdquo; button on the Measurements panel — the Mapbox map
              picker remains available for typed and pin-drawn measurements.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
